import { cn } from './ui/index.ts';

/** Espacement page — source unique. Bord-à-bord sur mobile, padding sur ≥ sm. */
export const pgPadX = 'px-2 sm:px-4';
/** Padding vertical : top minimal + bottom qui respecte le safe-area-inset
 *  (home indicator iOS PWA, barre de navigation Android). */
export const pgPadMainY =
  'pt-2 pb-[max(env(safe-area-inset-bottom,0px),0.75rem)] sm:pt-3 sm:pb-[max(env(safe-area-inset-bottom,0px),1rem)]';
export const pgGap = 'gap-2 sm:gap-3';

/** Hauteur fixe de toutes les barres du haut (en-tête + toolbars). */
export const pgBarH = 'h-14';

/** Barre du haut : une seule ligne, hauteur fixe 56px. Padding mobile réduit. */
export const pgBar = cn(
  'flex shrink-0 flex-row items-center gap-2 overflow-hidden border-b border-sf-border bg-sf-surface',
  'px-2 sm:px-4',
  pgBarH,
);

/** Contenu principal d’une barre (gauche). */
export const pgBarStart = 'min-w-0 flex-1';

/** Actions alignées à droite. */
export const pgBarActions = 'ml-auto flex shrink-0 items-center gap-1.5';

/** Contenu défilant horizontalement dans une barre (agenda, filtres…). */
export const toolbarScrollRow = cn(
  'flex h-full min-w-0 flex-1 items-center gap-2 overflow-x-auto',
  '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
);

export const pg = {
  page: 'flex min-h-0 flex-1 flex-col overflow-hidden bg-sf-surface-alt',
  toolbar: pgBar,
  header: pgBar,
  content: cn('min-h-0 flex-1 overflow-auto', pgPadX, pgPadMainY),
  main: cn('flex min-h-0 flex-1 flex-col overflow-hidden', pgPadX, pgPadMainY),
  mainRow: cn('flex min-h-0 flex-1', pgGap),
  gridCards:
    'grid min-h-0 auto-rows-min grid-cols-1 gap-3.5 sm:grid-cols-[repeat(auto-fill,minmax(min(100%,340px),1fr))]',
  scheduler:
    'flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-sf-border bg-white shadow-[var(--sf-shadow-sm)]',
  schedulerHeader: 'flex border-b border-sf-border bg-sf-surface-alt',
  schedulerBody: 'min-h-0 flex-1 overflow-hidden',
} as const;

/** Accent plein : activation (Terminer, filtre actif…) ou CTA critique (Nouveau patient). */
export const headerBtnAccent = cn(
  'inline-flex h-10 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-sf-primary bg-sf-primary px-3.5 font-[inherit] text-[14px] font-semibold text-white shadow-[0_1px_2px_rgba(79,70,229,.25)]',
  'lg:h-8 lg:rounded-md lg:px-3.5 lg:text-[12.5px]',
);

/** Action barre du haut / toolbar — contour blanc (Nouveau RDV, etc.). */
export const headerBtnSecondary = cn(
  'inline-flex h-10 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-sf-border-strong bg-white px-3.5 font-[inherit] text-[14px] font-medium text-sf-text',
  'lg:h-8 lg:rounded-md lg:px-3 lg:text-[12.5px]',
);

/** Action destructive — fond rouge plein, symétrique au headerBtnAccent. */
export const headerBtnDanger = cn(
  'inline-flex h-10 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-sf-danger bg-sf-danger px-3.5 font-[inherit] text-[14px] font-semibold text-white shadow-[0_1px_2px_rgba(220,38,38,.25)]',
  'lg:h-8 lg:rounded-md lg:px-3.5 lg:text-[12.5px]',
);

export const inputSearch =
  'flex h-10 w-full min-w-0 items-center gap-2 rounded-lg border border-sf-border bg-white px-3 lg:h-8 lg:rounded-md lg:px-2.5';

/** Champ recherche dans une toolbar (hauteur barre). */
export const toolbarSearch = cn(inputSearch, 'min-w-0 flex-1 sm:max-w-md');

export const inputPlain =
  'min-h-0 min-w-0 flex-1 border-none bg-transparent font-[inherit] text-sf-text outline-none';

export const pm = {
  btnPrimary:
    'inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-lg border border-sf-primary bg-sf-primary px-3.5 font-[inherit] text-[14px] font-semibold text-white lg:h-[30px] lg:rounded-md lg:px-3 lg:text-[12.5px]',
  btnSecondary:
    'inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-lg border border-sf-border-strong bg-white px-3.5 font-[inherit] text-[14px] font-medium text-sf-text lg:h-[30px] lg:rounded-md lg:px-3 lg:text-[12.5px]',
  btnCompact: 'h-[26px] px-2.5 text-[11.5px]',
  btnCompactTight: 'h-[26px] px-2 text-[11.5px]',
  btnDanger:
    'inline-flex h-10 cursor-pointer items-center rounded-lg border-none bg-red-600 px-3.5 font-[inherit] text-[14px] font-semibold text-white lg:h-[30px] lg:rounded-md lg:px-3 lg:text-[12.5px]',
  btnGhostRow:
    'inline-flex h-[26px] cursor-pointer items-center rounded-[5px] border border-sf-border-strong bg-transparent px-2 font-[inherit] text-[11.5px] text-sf-text',
  mediaCard:
    'cursor-pointer overflow-hidden rounded-lg border border-sf-border bg-sf-surface transition-[transform,box-shadow,border-color] duration-150 hover:-translate-y-px hover:border-sf-primary-100 hover:shadow-[var(--sf-shadow-md)]',
  historyRow:
    'rounded-md border border-sf-border bg-sf-surface-alt px-3.5 py-2.5 transition-[border-color,background-color] duration-150 hover:border-sf-primary-100 hover:bg-white',
  historyDot:
    'border-[color:var(--pm-history-accent)] text-[color:var(--pm-history-accent)]',
  input:
    'h-10 w-full rounded-lg border border-sf-border-strong bg-white px-3 text-[16px] text-sf-text font-[inherit] outline-none lg:h-[34px] lg:rounded-md lg:px-2.5 lg:text-[13px]',
  textarea:
    'min-h-[80px] w-full resize-y rounded-lg border border-sf-border-strong bg-white p-3 text-[16px] text-sf-text font-[inherit] outline-none lg:rounded-md lg:p-2.5 lg:text-[13px]',
} as const;

export function pillBtn(active: boolean) {
  return cn(
    'inline-flex h-10 shrink-0 cursor-pointer items-center gap-1 rounded-lg border px-3 text-[14px] font-medium whitespace-nowrap lg:h-8 lg:rounded-md lg:px-2.5 lg:text-xs',
    active
      ? 'border-sf-primary bg-sf-primary text-white'
      : 'border-sf-border-strong bg-white text-sf-text',
  );
}

export function secondaryBtnLg() {
  return cn(headerBtnSecondary, 'w-full justify-center text-xs sm:w-auto');
}

/** CTA critique (ex. Nouveau patient). */
export function accentBtnLg() {
  return cn(headerBtnAccent, 'w-full justify-center text-xs sm:w-auto');
}

/** Toolbar secondaire — outline blanc (préférer accentBtnLg si action majeure). */
export function primaryBtnLg() {
  return secondaryBtnLg();
}

const borderedIconBtn =
  'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg border border-sf-border-strong bg-white font-[inherit] text-sf-text-secondary transition-colors hover:bg-gray-100 lg:rounded-md';

export function miniIconBtn() {
  return cn(borderedIconBtn, 'h-10 w-10 lg:h-8 lg:w-8');
}

/** Flèche de navigation (date, filtres, vues…) — hauteur alignée avec navCenterBtn. */
export function navArrowBtn() {
  return cn(
    borderedIconBtn,
    'inline-flex h-10 w-10 shrink-0 items-center justify-center lg:h-8 lg:w-8',
  );
}

/** Bouton central des steppers (Aujourd'hui, filtre actif, vue Jour…). */
export function navCenterBtn(active = false) {
  return cn(
    'inline-flex h-10 shrink-0 cursor-pointer items-center justify-center rounded-lg border px-3 text-[14px] font-semibold whitespace-nowrap transition-colors lg:h-8 lg:rounded-md lg:px-2.5 lg:text-[11px]',
    active
      ? 'border-sf-primary bg-sf-primary text-white'
      : 'border-sf-border-strong bg-white text-sf-text-secondary hover:bg-gray-50',
  );
}

/** Toggle sidebar / panneau latéral agenda (bordé, comme miniIconBtn). */
export const shellIconBtn = cn(borderedIconBtn, 'h-10 w-10 lg:h-8 lg:w-8');

/** Icônes top bar sans bordure (aide, notifications, recherche mobile…). */
export const topBarGhostIconBtn = cn(
  'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent font-[inherit] text-sf-text-secondary transition-colors hover:bg-gray-100 lg:rounded-md',
  'h-10 w-10 lg:h-8 lg:w-8',
);

export function miniBtn() {
  return 'inline-flex h-8 cursor-pointer items-center justify-center rounded-md border border-sf-border bg-white px-2.5 text-[12.5px] font-medium text-sf-text-secondary lg:h-6 lg:rounded lg:px-2 lg:text-[11.5px]';
}

export function secondaryBtn() {
  return 'inline-flex h-10 cursor-pointer items-center rounded-lg border border-sf-border-strong bg-white px-3.5 text-[14px] font-medium text-sf-text lg:h-7 lg:rounded-[5px] lg:px-3 lg:text-xs';
}

export function accentBtn() {
  return 'inline-flex h-10 cursor-pointer items-center rounded-lg border border-sf-primary bg-sf-primary px-3.5 text-[14px] font-semibold text-white lg:h-7 lg:rounded-[5px] lg:px-3 lg:text-xs';
}

/** Widgets — outline par défaut. */
export function primaryBtn() {
  return secondaryBtn();
}

export function mobileListCard(active?: boolean) {
  return cn(
    'flex w-full cursor-pointer items-center gap-3 border-none px-4 py-3.5 text-left font-[inherit] transition-colors',
    active
      ? 'bg-sf-primary-50 hover:bg-sf-primary-50'
      : 'bg-white hover:bg-gray-50',
  );
}
